/**
 * Collection page - main card inventory view
 */
import { useLocalStorage } from '@uidotdev/usehooks';
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaFileExport, FaFileImport, FaPlus } from 'react-icons/fa';
import { IoAdd, IoCheckboxOutline, IoGrid, IoList } from 'react-icons/io5';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import Button from '@/components/Button/Button';
import ConfirmActionModal from '@/components/CardDetails/ConfirmActionModal';
import EmptyState from '@/components/EmptyState/EmptyState';
import Search from '@/components/Search/Search';
import SortBySelector from '@/components/SortBySelector/SortBySelector';
import Spinner from '@/components/Spinner/Spinner';
import ViewControlsBar from '@/components/ViewControlsBar/ViewControlsBar';
import { useBulkAddOrUpdateDeckCardsMutation, useDecksQuery } from '@/features/decks/hooks/api';
import { allDeckCardsAtom, cardOracleIdsByDeckAtom, cardOracleIdsInDecksAtom } from '@/features/decks/state/allDeckCards';
import type { SidebarWidth } from '@/features/folders/components/FolderSidebar';
import FolderSidebar from '@/features/folders/components/FolderSidebar';
import { useFoldersQuery } from '@/features/folders/hooks/api';
import ExportModal from '@/features/import-export/components/ExportModal';
import ImportModal from '@/features/import-export/components/ImportModal';
import { listedOracleIdsAtom, oracleIdsByListAtom } from '@/features/lists/state/listsAtom';
import useUserSettings from '@/features/settings/hooks/api';
import { starredOracleIdsAtom } from '@/features/starred/state/starredAtom';
import { matchesSearchQuery } from '@/lib/cardSearch';
import { getCmc, getOracleId } from '@/lib/cardUtils';
import { calculateTotalValue, formatCurrency } from '@/lib/price';
import { collectionAvailableByOracleIdAtom } from '@/state/cardAvailability';
import type { CardRoleId, Color, EnrichedCollectionCard, ViewMode } from '@/types/card';
import type { DeckZone } from '@/types/deck';

import AddToCollectionModal from './components/AddToCollectionModal';
import BulkActionBar from './components/BulkActionBar';
import BulkEditModal from './components/BulkEditModal';
import CardDetailsPanel from './components/CardDetailsPanel/CardDetailsPanel';
import FolderPickerModal from './components/CardDetailsPanel/FolderPickerModal';
import CollectionColumnVisibilityDropdown from './components/CollectionColumnVisibilityDropdown';
import CollectionGrid from './components/CollectionGrid';
import CollectionTable from './components/CollectionTable';
import DeckPickerModal from './components/DeckPickerModal';
import FilterPanel from './components/FilterPanel';
import { useBatchDeleteCardsMutation, useCollectionQuery, useMoveToFolderMutation } from './hooks/api';
import useCollectionFilters from './hooks/useCollectionFilters';
import useCollectionSort, { createSortComparator, SORT_FIELD_LABELS, type SortField } from './hooks/useCollectionSort';
import useSelectedCard from './hooks/useSelectedCard';
import { computeBulkDeckAdditions } from './lib/bulkDeckAdd';

/** WUBRG order for building color identity key strings */
const COLOR_IDENTITY_ORDER: Color[] = ['W', 'U', 'B', 'R', 'G'];

const COLLECTION_SORT_OPTIONS: { value: SortField; label: string }[] = [
  { value: 'quantity', label: SORT_FIELD_LABELS.quantity },
  { value: 'name', label: SORT_FIELD_LABELS.name },
  { value: 'type', label: SORT_FIELD_LABELS.type },
  { value: 'color', label: SORT_FIELD_LABELS.color },
  { value: 'cmc', label: SORT_FIELD_LABELS.cmc },
  { value: 'rarity', label: SORT_FIELD_LABELS.rarity },
  { value: 'edhrecRank', label: SORT_FIELD_LABELS.edhrecRank },
  { value: 'roles', label: SORT_FIELD_LABELS.roles },
  { value: 'set', label: SORT_FIELD_LABELS.set },
  { value: 'folder', label: SORT_FIELD_LABELS.folder },
  { value: 'price', label: SORT_FIELD_LABELS.price },
  { value: 'condition', label: SORT_FIELD_LABELS.condition },
  { value: 'language', label: SORT_FIELD_LABELS.language },
  { value: 'finish', label: SORT_FIELD_LABELS.finish },
  { value: 'dateAdded', label: SORT_FIELD_LABELS.dateAdded },
];

const SIDEBAR_WIDTH_KEY = 'mtgcraft:sidebar-width';

/**
 * Migrate old sidebar values to new two-state format
 */
function migrateSidebarWidth(value: unknown): SidebarWidth {
  if (value === 'true' || value === true) return 'collapsed';
  if (value === 'collapsed') return 'collapsed';
  return 'normal';
}

/** Debounce delay for search input (ms) */
const SEARCH_DEBOUNCE_MS = 750;

export default function CollectionPage(): ReactElement {
  const { settings } = useUserSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<ViewMode>(settings.defaultView);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [storedWidth, setStoredWidth] = useLocalStorage<SidebarWidth | string>(SIDEBAR_WIDTH_KEY, 'normal');

  // Selection mode state
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedCardIds, setSelectedCardIds] = useState<Set<string>>(new Set());
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [isFolderPickerOpen, setIsFolderPickerOpen] = useState(false);
  const [isDeckPickerOpen, setIsDeckPickerOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const { clearSelection: clearDetailsPanel } = useSelectedCard();
  const moveToFolder = useMoveToFolderMutation();
  const batchDelete = useBatchDeleteCardsMutation();
  const bulkAddToDeck = useBulkAddOrUpdateDeckCardsMutation();
  const { data: decks } = useDecksQuery();
  const allDeckCards = useAtomValue(allDeckCardsAtom);

  // Sync view mode with settings on first load
  useEffect(() => {
    setViewMode(settings.defaultView);
  }, [settings.defaultView]);

  // Migrate old boolean values and validate
  const sidebarWidth = migrateSidebarWidth(storedWidth);

  const { filters, setSearch, setFilter, clearFilters, hasActiveFilters } = useCollectionFilters();

  // Apply URL query params as filters and clear them
  useEffect(() => {
    const setsParam = searchParams.get('sets');
    if (setsParam) {
      setFilter('sets', [setsParam]);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, setFilter]);

  // Handle debounced search from Search component
  const handleSearch = useCallback(
    (query: string) => {
      setSearch(query);
    },
    [setSearch],
  );

  const { sort, setField: setSortField, toggleDirection: toggleSortDirection } = useCollectionSort();
  const { data: cards, isLoading, error } = useCollectionQuery();

  // ── Selection mode helpers ──

  // Track previous folderId to detect folder changes
  const prevFolderIdRef = useRef(filters.folderId);

  // Clear selection on folder change
  useEffect(() => {
    if (prevFolderIdRef.current !== filters.folderId) {
      prevFolderIdRef.current = filters.folderId;
      setSelectedCardIds(new Set());
      setIsSelectionMode(false);
    }
  }, [filters.folderId]);

  // Clear selection on unmount (route navigation)
  useEffect(() => {
    return () => {
      setSelectedCardIds(new Set());
      setIsSelectionMode(false);
    };
  }, []);

  // Escape key clears selection
  useEffect(() => {
    if (!isSelectionMode) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedCardIds(new Set());
        setIsSelectionMode(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSelectionMode]);

  const handleToggleSelectionMode = useCallback(() => {
    setIsSelectionMode((prev) => {
      if (!prev) {
        // Entering selection mode — close details panel
        clearDetailsPanel();
      } else {
        // Exiting selection mode — clear selection
        setSelectedCardIds(new Set());
      }
      return !prev;
    });
  }, [clearDetailsPanel]);

  const handleToggleCard = useCallback((cardId: string) => {
    setSelectedCardIds((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else {
        next.add(cardId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((ids: string[]) => {
    setSelectedCardIds(new Set(ids));
  }, []);

  const handleDeselectAll = useCallback(() => {
    setSelectedCardIds(new Set());
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedCardIds(new Set());
    setIsSelectionMode(false);
  }, []);

  const handleBulkEditSuccess = useCallback(() => {
    setSelectedCardIds(new Set());
    setIsSelectionMode(false);
  }, []);

  const handleFolderMoveSelect = useCallback(
    (folderId: string | null) => {
      const ids = Array.from(selectedCardIds);
      moveToFolder.mutate(
        { cardIds: ids, folderId },
        {
          onSuccess: () => {
            toast.success(`Moved ${ids.length.toLocaleString()} cards`);
            setIsFolderPickerOpen(false);
            setSelectedCardIds(new Set());
            setIsSelectionMode(false);
          },
          onError: (err) => {
            toast.error(`Failed to move cards: ${err instanceof Error ? err.message : 'Unknown error'}`);
          },
        },
      );
    },
    [selectedCardIds, moveToFolder],
  );

  const handleBulkDelete = useCallback(() => {
    const ids = Array.from(selectedCardIds);
    batchDelete.mutate(
      { cardIds: ids },
      {
        onSuccess: () => {
          toast.success(`Deleted ${ids.length.toLocaleString()} cards`);
          setIsDeleteConfirmOpen(false);
          setSelectedCardIds(new Set());
          setIsSelectionMode(false);
        },
        onError: (err) => {
          toast.error(`Failed to delete cards: ${err instanceof Error ? err.message : 'Unknown error'}`);
        },
      },
    );
  }, [selectedCardIds, batchDelete]);

  const starredOracleIds = useAtomValue(starredOracleIdsAtom);
  const listedOracleIds = useAtomValue(listedOracleIdsAtom);
  const oracleIdsByList = useAtomValue(oracleIdsByListAtom);
  const oracleIdsInDecks = useAtomValue(cardOracleIdsInDecksAtom);
  const oracleIdsByDeck = useAtomValue(cardOracleIdsByDeckAtom);
  const availableByOracleId = useAtomValue(collectionAvailableByOracleIdAtom);

  const handleCollapseSidebar = useCallback(() => {
    setStoredWidth('collapsed');
  }, [setStoredWidth]);

  const handleExpandSidebar = useCallback(() => {
    setStoredWidth('normal');
  }, [setStoredWidth]);

  // Memoize filtered cards - only recalculate when cards or filters change
  const filteredCards = useMemo(() => {
    return (cards ?? []).filter((item: EnrichedCollectionCard) => {
      // Search filter — uses pre-computed lowercase fields to avoid per-card per-keystroke toLowerCase()
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        if (
          !matchesSearchQuery(
            { nameLower: item.card.name_lower, typeLineLower: item.card.type_line_lower, oracleTextLower: item.oracle_text_lower },
            searchLower,
          )
        )
          return false;
      }

      // Color filter
      if (filters.colors && filters.colors.length > 0) {
        const cardColors = item.card.color_identity;
        const hasMatchingColor = filters.colors.some((c: Color) => cardColors.includes(c));
        if (!hasMatchingColor) return false;
      }

      // Special color filter (Multicolor / Colorless)
      if (filters.specialColors && filters.specialColors.length > 0) {
        const cardColors = item.card.color_identity;
        const isMulticolor = cardColors.length > 1;
        const isColorless = cardColors.length === 0;

        const matchesSpecialColor = filters.specialColors.some((sc) => {
          if (sc === 'multicolor') return isMulticolor;
          if (sc === 'colorless') return isColorless;
          return false;
        });

        if (!matchesSpecialColor) return false;
      }

      // Color identity (exact match) filter
      if (filters.colorIdentities && filters.colorIdentities.length > 0) {
        const cardKey = COLOR_IDENTITY_ORDER.filter((c) => item.card.color_identity.includes(c)).join('');
        if (!filters.colorIdentities.includes(cardKey)) return false;
      }

      // Supertype filter (Legendary)
      if (filters.superTypes && filters.superTypes.length > 0) {
        const matchesSuperType = filters.superTypes.some((st) => item.classification.super_types.includes(st));
        if (!matchesSuperType) return false;
      }

      // Card type filter (Creature, Instant, etc.)
      if (filters.cardTypes && filters.cardTypes.length > 0) {
        const matchesAnyType = filters.cardTypes.some((ct) => item.classification.main_types.includes(ct));
        if (!matchesAnyType) return false;
      }

      // Card category filter (Traditional / specific nontraditional types)
      if (filters.cardCategories && filters.cardCategories.length > 0) {
        const matchesCategory = filters.cardCategories.some((cat) => {
          if (cat === 'traditional') return !item.classification.is_nontraditional;
          return item.classification.nontraditional_type === cat;
        });
        if (!matchesCategory) return false;
      }

      // Rarity filter
      if (filters.rarities && filters.rarities.length > 0) {
        const cardRarity = item.card.rarity;
        const standardRarities = ['common', 'uncommon', 'rare', 'mythic'];
        const isOtherRarity = !standardRarities.includes(cardRarity);

        const matchesRarity = filters.rarities.some((filterRarity) => {
          if (filterRarity === 'other') return isOtherRarity;
          return filterRarity === cardRarity;
        });

        if (!matchesRarity) return false;
      }

      // CMC filter
      if (filters.cmcValues && filters.cmcValues.length > 0) {
        const cardCmc = getCmc(item.card) ?? 0;
        const matchesCmc = filters.cmcValues.some((cmcFilter) => {
          if (cmcFilter === '6+') return cardCmc >= 6;
          return cardCmc === cmcFilter;
        });

        if (!matchesCmc) return false;
      }

      // Set filter
      if (filters.sets && filters.sets.length > 0) {
        if (!filters.sets.includes(item.card.set)) return false;
      }

      // Folder filter
      if (filters.folderId !== undefined) {
        if (item.folder_id !== filters.folderId) return false;
      }

      // Condition filter
      if (filters.conditions && filters.conditions.length > 0) {
        if (!filters.conditions.includes(item.condition)) return false;
      }

      // Finish filter
      if (filters.finishes && filters.finishes.length > 0) {
        if (!filters.finishes.includes(item.finish)) return false;
      }

      // Starred filter
      if (filters.starredOnly) {
        if (!getOracleId(item.card) || !starredOracleIds.has(getOracleId(item.card)!)) return false;
      }

      if (filters.gameChangersOnly && !(item.card.game_changer ?? false)) return false;

      if (filters.availableOnly) {
        const oracleId = getOracleId(item.card);
        if (!oracleId || (availableByOracleId.get(oracleId) ?? 0) <= 0) return false;
      }

      // List membership filter
      if (filters.inListFilter) {
        const oracleId = getOracleId(item.card);
        if (!oracleId) return false;
        if (filters.listIdFilter) {
          // Filter by specific list
          const listOracleIds = oracleIdsByList.get(filters.listIdFilter);
          const isInList = listOracleIds?.has(oracleId) ?? false;
          if (filters.inListFilter === 'in-list' && !isInList) return false;
          if (filters.inListFilter === 'not-in-list' && isInList) return false;
        } else {
          // Filter by any list
          const isInAnyList = listedOracleIds.has(oracleId);
          if (filters.inListFilter === 'in-list' && !isInAnyList) return false;
          if (filters.inListFilter === 'not-in-list' && isInAnyList) return false;
        }
      }

      // Deck membership filter
      if (filters.inDeckFilter) {
        const oracleId = getOracleId(item.card);
        if (!oracleId) return false;
        if (filters.deckIdFilter) {
          // Filter by specific deck
          const deckOracleIds = oracleIdsByDeck.get(filters.deckIdFilter);
          const isInDeck = deckOracleIds?.has(oracleId) ?? false;
          if (filters.inDeckFilter === 'in-deck' && !isInDeck) return false;
          if (filters.inDeckFilter === 'not-in-deck' && isInDeck) return false;
        } else {
          // Filter by any deck
          const isInAnyDeck = oracleIdsInDecks.has(oracleId);
          if (filters.inDeckFilter === 'in-deck' && !isInAnyDeck) return false;
          if (filters.inDeckFilter === 'not-in-deck' && isInAnyDeck) return false;
        }
      }

      // Role filter — uses pre-computed classification from enrichment
      if (filters.roles && filters.roles.length > 0) {
        const matchesAnyRole = filters.roles.some((r) => item.classification.active_roles.includes(r as CardRoleId));
        if (!matchesAnyRole) return false;
      }

      // Keyword filter — match against ScryfallCard keywords
      if (filters.keywords && filters.keywords.length > 0) {
        const keywordsLower = filters.keywords.map((k) => k.toLowerCase());
        const cardKeywords = item.card.keywords?.map((k: string) => k.toLowerCase()) ?? [];
        const matchesKeyword = keywordsLower.some((kw) => cardKeywords.includes(kw));
        if (!matchesKeyword) return false;
      }

      return true;
    });
  }, [cards, filters, starredOracleIds, listedOracleIds, oracleIdsByList, oracleIdsInDecks, oracleIdsByDeck, availableByOracleId]);

  // Memoize sort comparator - only recreate when sort or currency changes
  const sortComparator = useMemo(() => createSortComparator(sort, settings.currency), [sort, settings.currency]);

  // Memoize sorted cards - only recalculate when filtered cards or sort changes
  const sortedCards = useMemo(() => {
    return [...filteredCards].sort(sortComparator);
  }, [filteredCards, sortComparator]);

  const handleAddToDeck = useCallback(
    (deckId: string, zone: DeckZone) => {
      const selectedCards = sortedCards.filter((c) => selectedCardIds.has(c.id));
      if (selectedCards.length === 0) return;

      const deck = decks?.find((d) => d.id === deckId);
      const deckFormat = deck?.format ?? null;
      const deckCardsData = allDeckCards.data ?? [];

      const { toAdd, toUpdate, skipped } = computeBulkDeckAdditions(selectedCards, deckId, deckFormat, deckCardsData, zone);

      const totalAdding = toAdd.length + toUpdate.length;

      bulkAddToDeck.mutate(
        { deckId, toAdd, toUpdate },
        {
          onSuccess: () => {
            const deckName = deck?.name ?? 'deck';
            if (skipped > 0) {
              toast.success(
                `Added ${totalAdding.toLocaleString()} cards to ${deckName}, ${skipped.toLocaleString()} skipped (at copy limit)`,
              );
            } else {
              toast.success(`Added ${totalAdding.toLocaleString()} cards to ${deckName}`);
            }
            setIsDeckPickerOpen(false);
            setSelectedCardIds(new Set());
            setIsSelectionMode(false);
          },
          onError: (err) => {
            toast.error(`Failed to add cards: ${err instanceof Error ? err.message : 'Unknown error'}`);
          },
        },
      );
    },
    [sortedCards, selectedCardIds, decks, allDeckCards.data, bulkAddToDeck],
  );

  // Calculate stats
  const totalCards = sortedCards.reduce((sum: number, item: EnrichedCollectionCard) => sum + item.quantity, 0);
  const uniqueCards = sortedCards.length;
  const totalValue = calculateTotalValue(sortedCards, settings.currency);

  // Scoped total: count within current folder (or entire collection if no folder)
  const scopedTotal = useMemo(() => {
    if (!cards) return 0;
    if (filters.folderId === undefined) return cards.length;
    return cards.filter((c) => c.folder_id === filters.folderId).length;
  }, [cards, filters.folderId]);

  // Build folder path for header title
  const { data: folders } = useFoldersQuery();
  const folderLabel = useMemo(() => {
    if (filters.folderId === undefined) return null;
    if (filters.folderId === null) return 'Unfiled';
    if (!folders) return null;
    const buildPath = (id: string): string[] => {
      const folder = folders.find((f) => f.id === id);
      if (!folder) return [];
      if (folder.parent_id) return [...buildPath(folder.parent_id), folder.name];
      return [folder.name];
    };
    const path = buildPath(filters.folderId);
    return path.length > 0 ? path.join(' / ') : null;
  }, [filters.folderId, folders]);

  // Handle folder selection
  const handleSelectFolder = (folderId: string | null | undefined) => {
    setFilter('folderId', folderId);
  };

  return (
    <div className="flex h-full">
      {/* Folder Sidebar */}
      <FolderSidebar
        cards={cards ?? []}
        selectedFolderId={filters.folderId}
        onSelectFolder={handleSelectFolder}
        width={sidebarWidth}
        onCollapse={handleCollapseSidebar}
        onExpand={handleExpandSidebar}
      />

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
          <div>
            <h1 className="text-xl font-semibold text-zinc-100">
              Collection
              {folderLabel && <span className="ml-2 text-base font-normal text-zinc-400">/ {folderLabel}</span>}
            </h1>
            {cards && cards.length > 0 && (
              <p className="mt-0.5 text-sm text-zinc-500">
                {uniqueCards.toLocaleString()} unique cards
                {uniqueCards !== scopedTotal && ` of ${scopedTotal.toLocaleString()} total`}
                {' · '}
                {totalCards.toLocaleString()} copies
                {totalValue > 0 && <span className="text-zinc-400"> · {formatCurrency(totalValue, settings.currency)}</span>}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button icon={<FaPlus />} text="Add Card" color="primary" onClick={() => setIsAddModalOpen(true)} />
            <Button icon={<FaFileImport />} text="Import" color="secondary" onClick={() => setIsImportOpen(true)} />
            <Button
              icon={<FaFileExport />}
              text="Export"
              color="secondary"
              onClick={() => setIsExportOpen(true)}
              disabled={sortedCards.length === 0}
            />
          </div>
        </header>

        {/* Controls bar */}
        <ViewControlsBar
          left={
            <Search
              onSearch={handleSearch}
              debounceMs={SEARCH_DEBOUNCE_MS}
              placeholder="Search cards..."
              className="max-w-75 min-w-50 flex-1"
            />
          }
          right={
            <>
              {/* Column visibility (table view only) */}
              {viewMode === 'table' && <CollectionColumnVisibilityDropdown />}

              {/* Sort */}
              <SortBySelector
                value={sort.field}
                direction={sort.direction}
                options={COLLECTION_SORT_OPTIONS}
                onChange={(field, direction) => {
                  if (field !== sort.field) {
                    setSortField(field);
                  } else if (direction !== sort.direction) {
                    toggleSortDirection();
                  }
                }}
                variant="dropdown"
              />

              {/* Selection mode toggle */}
              <button
                onClick={handleToggleSelectionMode}
                className={clsx(
                  'rounded-md p-2 transition-colors',
                  isSelectionMode ? 'bg-aether/20 text-aether' : 'text-ash hover:text-pearl',
                )}
                aria-label={isSelectionMode ? 'Exit selection mode' : 'Enter selection mode'}
                title={isSelectionMode ? 'Exit selection mode' : 'Select cards'}
              >
                <IoCheckboxOutline className="h-5 w-5" />
              </button>

              {/* View toggle */}
              <div className="bg-slate flex items-center rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={clsx(
                    'rounded-md p-2 transition-colors',
                    viewMode === 'grid' ? 'bg-storm text-pearl' : 'text-ash hover:text-pearl',
                  )}
                  aria-label="Grid view"
                >
                  <IoGrid className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={clsx(
                    'rounded-md p-2 transition-colors',
                    viewMode === 'table' ? 'bg-storm text-pearl' : 'text-ash hover:text-pearl',
                  )}
                  aria-label="Table view"
                >
                  <IoList className="h-5 w-5" />
                </button>
              </div>
            </>
          }
        />

        {/* Filters */}
        <FilterPanel filters={filters} setFilter={setFilter} clearFilters={clearFilters} hasActiveFilters={hasActiveFilters} />

        {/* Bulk Action Bar */}
        {isSelectionMode && (
          <BulkActionBar
            selectedCount={selectedCardIds.size}
            totalCount={sortedCards.length}
            onSelectAll={() => handleSelectAll(sortedCards.map((c) => c.id))}
            onEdit={() => setIsBulkEditOpen(true)}
            onMoveToFolder={() => setIsFolderPickerOpen(true)}
            onAddToDeck={() => setIsDeckPickerOpen(true)}
            onDelete={() => setIsDeleteConfirmOpen(true)}
            onClearSelection={handleDeselectAll}
            onClose={handleClearSelection}
          />
        )}

        {/* Content */}
        <div className={clsx('flex-1 p-6', viewMode === 'table' ? 'flex flex-col overflow-hidden' : 'overflow-auto')}>
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Spinner size="lg" />
            </div>
          ) : error ? (
            <EmptyState title="Error loading collection" description={error instanceof Error ? error.message : 'An error occurred'} />
          ) : filteredCards.length === 0 ? (
            <EmptyState
              icon={<IoAdd className="h-8 w-8" />}
              title={hasActiveFilters ? 'No cards match filters' : 'Your collection is empty'}
              description={
                hasActiveFilters
                  ? 'Try adjusting your search or filters'
                  : 'Import cards from Dragon Shield, DelverLens, or other apps to get started'
              }
              action={
                hasActiveFilters ? (
                  <button onClick={clearFilters} className="text-aether hover:text-aether-glow text-sm transition-colors">
                    Clear filters
                  </button>
                ) : undefined
              }
            />
          ) : viewMode === 'grid' ? (
            <CollectionGrid
              cards={sortedCards}
              isSelectionMode={isSelectionMode}
              selectedCardIds={selectedCardIds}
              onToggleCard={handleToggleCard}
            />
          ) : (
            <CollectionTable
              cards={sortedCards}
              sort={sort}
              onSortChange={setSortField}
              isSelectionMode={isSelectionMode}
              selectedCardIds={selectedCardIds}
              onToggleCard={handleToggleCard}
              onSelectAll={handleSelectAll}
              onDeselectAll={handleDeselectAll}
            />
          )}
        </div>

        {/* Add/Import/Export Modals */}
        <AddToCollectionModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          defaultFolderId={typeof filters.folderId === 'string' ? filters.folderId : undefined}
        />
        <ImportModal isOpen={isImportOpen} onClose={() => setIsImportOpen(false)} />
        <ExportModal isOpen={isExportOpen} onClose={() => setIsExportOpen(false)} cards={sortedCards} />

        {/* Bulk Edit Modal */}
        <BulkEditModal
          isOpen={isBulkEditOpen}
          onClose={() => setIsBulkEditOpen(false)}
          selectedCardIds={Array.from(selectedCardIds)}
          onSuccess={handleBulkEditSuccess}
        />

        {/* Folder Picker Modal (bulk move) */}
        <FolderPickerModal
          isOpen={isFolderPickerOpen}
          onClose={() => setIsFolderPickerOpen(false)}
          currentFolderId={null}
          onSelect={handleFolderMoveSelect}
          isPending={moveToFolder.isPending}
        />

        {/* Deck Picker Modal (bulk add to deck) */}
        <DeckPickerModal
          isOpen={isDeckPickerOpen}
          onClose={() => setIsDeckPickerOpen(false)}
          onConfirm={handleAddToDeck}
          isPending={bulkAddToDeck.isPending}
          selectedCount={selectedCardIds.size}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmActionModal
          isOpen={isDeleteConfirmOpen}
          onClose={() => setIsDeleteConfirmOpen(false)}
          title="Delete Cards"
          description={`Delete ${selectedCardIds.size.toLocaleString()} cards? This cannot be undone.`}
          confirmLabel="Delete"
          pendingLabel="Deleting..."
          isPending={batchDelete.isPending}
          onConfirm={handleBulkDelete}
        />

        {/* Card Details Panel */}
        <CardDetailsPanel />
      </div>
    </div>
  );
}
